import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  IconButton, 
  Grid,
  CircularProgress,
  Alert,
  styled,
  TablePagination,
  InputAdornment,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Stack,
  Autocomplete,
  Menu, // ✅ 3 nokta menüsü için
  Dialog, // ✅ Onay modal için
  DialogTitle, // ✅ Onay modal için
  DialogContent, // ✅ Onay modal için
  DialogContentText, // ✅ Onay modal için
  DialogActions, // ✅ Onay modal için
  Snackbar, // ✅ Bildirim için
  Card,
  CardContent,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import FilterListIcon from '@mui/icons-material/FilterList';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { getBranches } from '../../../services/migration/branchDataService';
import { getSalesScreenOptions } from '../../../services/migration/salesScreenDataService';
import { useNavigate } from 'react-router-dom';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import ClearIcon from '@mui/icons-material/Clear';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentIcon from '@mui/icons-material/Payment';
import MoreVertIcon from '@mui/icons-material/MoreVert'; // ✅ 3 nokta ikonu
import DeleteIcon from '@mui/icons-material/Delete'; // ✅ Sil ikonu
import ReceiptIcon from '@mui/icons-material/Receipt'; // ✅ Fatura ikonu
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye'; // ✅ Görüntüleme ikonu
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import trLocale from 'date-fns/locale/tr';
import enLocale from 'date-fns/locale/en-US';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import CashboxTransactionLogsModal from './CashboxTransactionLogsModal'; // ✅ Log Modal
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import ProjectBadge from '../shared/ProjectBadge';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS, formatStatsCurrency } from '../shared/StatsCard';
import { useTranslation } from 'react-i18next';

// Kaydırılabilir ana içerik için styled component
const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto',
  height: 'calc(100vh - 64px)', // Header yüksekliğini çıkarıyoruz
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
}));

// Başlık alanı için styled component
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

// Kasa hareketi arayüzü
interface CashboxTransaction {
  id: string;
  cashboxId: string;
  cashboxName: string;
  type: 'INCOME' | 'EXPENSE'; // INCOME: Gelir, EXPENSE: Gider
  transactionType: string;
  amount: number;
  description: string;
  referenceId?: string;
  referenceType?: string;
  accountId?: string;
  accountName?: string;
  date: string; // Ana tarih (gün/ay/yıl)
  createdAt: string; // Tam zaman damgası (saat dahil)
  createdBy: string;
  currency: string;
  paymentMethod?: string;
  balanceBefore?: number; // İşlem öncesi kasa bakiyesi
  balanceAfter?: number; // İşlem sonrası kasa bakiyesi
  screenId?: string; // İşlemin yapıldığı satış ekranı ID'si
  screenName?: string; // İşlemin yapıldığı satış ekranı adı
  branchId?: string; // ✅ YENİ: Şube ID'si
  branchName?: string; // ✅ YENİ: Şube adı
  projectId?: number | null; // PYS: bağlı olduğu proje
  projectName?: string | null;
}

// İşlem türleri (const obje olarak güncellendi)
const TRANSACTION_TYPES = {
  CASH: 'cash',
  CREDIT_CARD: 'credit_card',
  DEBIT_CARD: 'DEBIT_CARD',
  BANK_TRANSFER: 'BANK_TRANSFER',
  COLLECTION: 'COLLECTION',
  PAYMENT: 'PAYMENT',
  DEPOSIT: 'DEPOSIT',
  WITHDRAWAL: 'WITHDRAWAL',
  EXPENSE: 'EXPENSE',
  INVOICE_PAYMENT: 'invoice_payment',
  QUICK_SALE: 'QUICK_SALE', // Yeni Hızlı Satış türü
  TRANSFER: 'transfer', // Kasa-Banka transfer işlemleri
  COLLECTION_FROM_ACCOUNT: 'COLLECTION_FROM_ACCOUNT', // Cariden tahsilat
  PAYMENT_TO_ACCOUNT: 'PAYMENT_TO_ACCOUNT' // Cariye ödeme
};

// Para birimi sembolünü döndür
const getCurrencySymbol = (currency: string): string => {
  switch(currency) {
    case 'TRY': return '₺';
    case 'USD': return '$';
    case 'EUR': return '€';
    case 'GBP': return '£';
    case 'JPY': return '¥';
    case 'CHF': return 'Fr.';
    case 'AUD': return 'A$';
    case 'CAD': return 'C$';
    case 'CNY': return '¥';
    default: return currency;
  }
};

const decodeMojibake = (value?: string | null): string => {
  if (!value) return '';
  let decoded = value;
  // Some strings arrive double-encoded from legacy sources.
  for (let i = 0; i < 2; i += 1) {
    if (!/[ÃÂÅÄ]/.test(decoded)) break;
    try {
      const next = decodeURIComponent(escape(decoded));
      if (!next || next === decoded) break;
      decoded = next;
    } catch {
      break;
    }
  }
  return decoded;
};

interface CashboxTransactionsProps {
  /** Şube yöneticisi paneli: yalnızca kendi şube kasaları, salt okunur (silme/log yok) */
  isManager?: boolean;
  managerBranchId?: string | null;
  /** Verilirse geri butonu sayfa yönlendirmesi yerine bunu çağırır */
  onBack?: () => void;
}

const CashboxTransactions: React.FC<CashboxTransactionsProps> = ({ isManager = false, managerBranchId = null, onBack }) => {
  const { t, i18n } = useTranslation('accounting');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';
  const pickerLocale = i18n.language?.toLowerCase().startsWith('en') ? enLocale : trLocale;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filteredTransactions, setFilteredTransactions] = useState<CashboxTransaction[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [sortColumn, setSortColumn] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filtre state'leri
  const [filterType, setFilterType] = useState<string>('all');
  const [filterCashbox, setFilterCashbox] = useState<string>('');
  // Para birimi filtresini kaldırıyoruz
  // const [filterCurrency, setFilterCurrency] = useState<string>('');
  
  // Yeni filtreler
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  
  // Filtre verileri
  const [cashboxes, setCashboxes] = useState<{id: string, name: string}[]>([]);
  const [currencies, setCurrencies] = useState<string[]>([]);
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Şirket ID'si
  const [companyId, setCompanyId] = useState<string | null>(null);

  // Excel indirme için state
  const [loadingExport, setLoadingExport] = useState(false);
  
  // Filtre paneli görünürlüğü için state (✅ Varsayılan olarak gizli)
  const [showFilters, setShowFilters] = useState(false);

  // ✅ API'den güncel özet verileri
  const [dailySummary, setDailySummary] = useState<{
    previousBalance: { income: number; expense: number; netBalance: number };
    period: { income: number; expense: number; total: number };
    currentBalance: number;
  } | null>(null);

  // Satış ekranları state'leri
  const [salesScreens, setSalesScreens] = useState<{ id: string; name: string; type: string }[]>([]);

  // ✅ YENİ: Şube filtreleme state'leri
  const [branches, setBranches] = useState<{ id: string; name: string }[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<{ id: string; name: string }[]>([]);

  // ✅ Silme menüsü için state'ler
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTransactionForDelete, setSelectedTransactionForDelete] = useState<CashboxTransaction | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // ✅ Log modalı için state
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);

  const getPaymentMethodName = (method?: string): string => {
    if (!method) return '-';
    const map: Record<string, string> = {
      cash: 'cash',
      credit_card: 'creditCard',
      bank_transfer: 'bankTransfer',
      check: 'check',
      debit_card: 'debitCard',
    };
    const key = map[method];
    return key ? t(`cash.transactionReport.paymentMethods.${key}`) : method;
  };

  const getTransactionTypeName = (transactionType?: string, fallbackType?: string): string => {
    const raw = (transactionType || fallbackType || '').toUpperCase();
    const map: Record<string, string> = {
      CASH: 'cash',
      CREDIT_CARD: 'creditCard',
      DEBIT_CARD: 'debitCard',
      BANK_TRANSFER: 'bankTransfer',
      COLLECTION: 'collection',
      PAYMENT: 'payment',
      DEPOSIT: 'deposit',
      WITHDRAWAL: 'withdrawal',
      EXPENSE: 'expense',
      INVOICE_PAYMENT: 'invoicePayment',
      QUICK_SALE: 'quickSale',
      TRANSFER: 'transfer',
      COLLECTION_FROM_ACCOUNT: 'collectionFromAccount',
      PAYMENT_TO_ACCOUNT: 'paymentToAccount',
      LOAN_DISBURSEMENT: 'loanDisbursement',
      LOAN_INSTALLMENT_PAYMENT: 'loanInstallmentPayment',
    };
    const key = map[raw];
    return key ? t(`cash.transactionReport.transactionTypes.${key}`) : (transactionType || fallbackType || t('cash.transactionReport.transactionTypes.other'));
  };

  // ✅ API'den kasa özet verilerini yükle (devreden bakiye, bugün, net bakiye)
  const loadDailySummary = async (companyId: string) => {
    try {
      // Query parametreleri oluştur
      const params = new URLSearchParams();
      
      if (startDate) {
        params.append('startDate', startDate.toISOString());
      }
      if (endDate) {
        // ✅ DÜZELTME: endDate günün SONUNA ayarlanmalı (23:59:59.999)
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        params.append('endDate', endOfDay.toISOString());
        console.log('📅 [SUMMARY] endDate günün sonuna ayarlandı:', endOfDay.toISOString());
      }
      if (filterCashbox) {
        params.append('cashboxId', filterCashbox);
      }

      // Tür filtresi (INCOME/EXPENSE)
      if (filterType && filterType !== 'all') {
        params.append('type', filterType);
      }

      // Şube filtresi
      if (selectedBranches.length > 0) {
        params.append('branchIds', selectedBranches.map(b => b.id).join(','));
      }

      const url = `${ACCOUNTING_API.CASHBOX_DAILY_SUMMARY}?${params.toString()}`;
      
      console.log('📊 [SUMMARY] Kasa özeti yükleniyor:', url);
      
      const response = await accountingApi.get(url);
      console.log('✅ [SUMMARY] Kasa özeti yüklendi:', response.data.data);
      
      setDailySummary(response.data.data);
    } catch (error) {
      console.error('❌ [SUMMARY] Kasa özeti yüklenirken hata:', error);
      // Hata durumunda null olarak kalacak
    }
  };

  // Satış ekranlarını yükle (SQL — Hızlı Satış + PYK)
  const loadSalesScreens = async (companyId: string) => {
    try {
      const screensArray = await getSalesScreenOptions(companyId);
      console.log(`Toplam ${screensArray.length} satış ekranı bulundu (SQL)`);
      setSalesScreens(screensArray);
      return screensArray;
    } catch (error) {
      console.error('Satış ekranları yüklenirken hata:', error);
      return [];
    }
  };

  // ✅ YENİ: Şubeleri yükle (Firebase'den)
  const loadBranches = async (companyId: string) => {
    try {
      console.log('Şubeler yükleniyor...');
      
      const branches = await getBranches(companyId);

      let branchesArray: { id: string; name: string }[] = (branches || [])
        .filter((b) => b.name)
        .map((b) => ({ id: b.id, name: b.name }));
      console.log(`${branchesArray.length} şube bulundu`);
      
      // Şubeleri isme göre sırala
      branchesArray.sort((a, b) => a.name.localeCompare(b.name));
      
      setBranches(branchesArray);
      
      return branchesArray;
    } catch (error) {
      console.error('Şubeler yüklenirken hata:', error);
      return [];
    }
  };

  // İlk yükleme: companyId, kasalar, ekranlar, şubeler
  useEffect(() => {
    const initLoad = async () => {
      try {
        setLoading(true);
        
        const uid = ownAuthSession.getCurrentUid();
        if (!uid) {
          setError('Kullanıcı oturumu açık değil.');
          setLoading(false);
          return;
        }
        
        const userData = await firebaseDataService.getUserData(uid);
        if (!userData) {
          setError('Kullanıcı bilgileri bulunamadı.');
          setLoading(false);
          return;
        }
        
        const cId = userData.companyId;
        setCompanyId(cId || null);
        
        if (!cId) {
          setError('Şirket bilgisi bulunamadı.');
          setLoading(false);
          return;
        }
        
        await Promise.all([
          loadCashboxes(cId),
          loadSalesScreens(cId),
          loadBranches(cId),
        ]);
        
        setLoading(false);
      } catch (err: any) {
        console.error('❌ Başlangıç verileri yüklenirken hata:', err);
        setError('Veriler yüklenirken bir hata oluştu: ' + err.message);
        setLoading(false);
      }
    };
    initLoad();
  }, []);

  // Server-side sayfalama ile kasa işlemlerini yükleme
  const loadTransactions = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);

      const response = await accountingApi.get(
        ACCOUNTING_API.CASHBOX_COMPANY_TRANSACTIONS_PAGED({
          page: page + 1,
          pageSize: rowsPerPage,
          searchTerm: searchTerm || undefined,
          sortColumn: sortColumn || undefined,
          sortDirection: sortDirection || 'desc',
          cashboxId: filterCashbox || undefined,
          type: filterType !== 'all' ? filterType : undefined,
          startDate: startDate ? startDate.toISOString() : undefined,
          endDate: endDate ? (() => { const d = new Date(endDate); d.setHours(23, 59, 59, 999); return d.toISOString(); })() : undefined,
          branchIds: selectedBranches.length > 0 ? selectedBranches.map(b => b.id).join(',') : undefined,
        })
      );

      const result = response.data;
      if (result.success && result.data) {
        const txList: CashboxTransaction[] = result.data.map((data: any) => ({
          id: data.id.toString(),
          cashboxId: data.cashboxId ? data.cashboxId.toString() : '',
          cashboxName: decodeMojibake(data.cashbox?.name || t('cash.transactionReport.unknownCashbox')),
          type: data.type || 'INCOME',
          transactionType: data.transactionType || '',
          amount: parseFloat(data.amount) || 0,
          description: data.expenseCategoryName
            ? `[${data.expenseCategoryName}] ${decodeMojibake(data.description || '')}`
            : decodeMojibake(data.description || ''),
          referenceId: data.referenceId,
          referenceType: data.referenceType,
          accountId: data.accountId ? data.accountId.toString() : undefined,
          accountName: decodeMojibake(data.accountName),
          date: data.date || data.createdAt,
          createdAt: data.createdAt || '',
          createdBy: data.createdBy || '',
          currency: data.currency || 'TRY',
          paymentMethod: data.paymentMethod,
          balanceBefore: parseFloat(data.balanceBefore) || 0,
          balanceAfter: parseFloat(data.balanceAfter) || 0,
          screenId: data.screenId || '',
          screenName: decodeMojibake(data.screenName || (data.screenId ? salesScreens.find(s => s.id === data.screenId)?.name : undefined) || '-'),
          branchId: data.branchId || '',
          branchName: decodeMojibake(data.branchName || ''),
          projectId: data.projectId ?? null,
          projectName: data.projectName ? decodeMojibake(data.projectName) : null
        }));

        setFilteredTransactions(txList);
        setTotalCount(result.totalCount || 0);
      } else {
        setFilteredTransactions([]);
        setTotalCount(0);
      }
    } catch (err: any) {
      console.error('❌ Kasa hareketleri yüklenirken hata:', err);
      setError(err.message || t('cash.transactionReport.errors.loadFailed'));
      setFilteredTransactions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [companyId, page, rowsPerPage, searchTerm, sortColumn, sortDirection, filterCashbox, filterType, startDate, endDate, selectedBranches, salesScreens]);

  // Server-side yükleme - debounce ile
  useEffect(() => {
    if (!companyId) return;
    const timer = setTimeout(() => {
      loadTransactions();
      // Özeti de yeniden yükle
      loadDailySummary(companyId);
    }, 400);
    return () => clearTimeout(timer);
  }, [loadTransactions]);

  // Filtre değiştiğinde sayfa sıfırla
  useEffect(() => {
    setPage(0);
  }, [searchTerm, filterType, filterCashbox, startDate, endDate, selectedBranches, sortColumn, sortDirection]);

  // Sayfalanmış işlemler (server tarafından sayfalanmış)
  const paginatedTransactions = filteredTransactions;
  
  // Kasaları yükle (filtre için - SQL'den)
  const loadCashboxes = async (companyId: string) => {
    try {
      console.log('📋 Kasalar filtre için SQL\'den yükleniyor...');
      
      const response = await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY);
      const sqlCashboxes = response.data.data || response.data;
      
      const cashboxList: {id: string, name: string}[] = (sqlCashboxes as any[])
        .filter((data: any) => !isManager || (!!managerBranchId && data.branchId === managerBranchId))
        .map((data: any) => ({
          id: data.id.toString(),
          name: data.name || t('cash.transactionReport.unnamedCashbox')
        }));
      
      // İsme göre sırala
      cashboxList.sort((a, b) => a.name.localeCompare(b.name));
      
      console.log('✅ Kasalar yüklendi (filtre için):', cashboxList.length, 'adet');
      
      setCashboxes(cashboxList);
    } catch (err) {
      console.error('❌ Kasalar yüklenirken hata:', err);
      setCashboxes([]);
    }
  };

  // Tarih formatla - işlemin gerçek tarihi (Date sütunu) gösterilir; Date yoksa createdAt'e düşülür (map'te)
  const formatDate = (dateString: string, transactionType?: string) => {
    try {
      let adjustedDateString = dateString;
      
      // ✅ SADECE transfer işlemlerinde UTC olarak işle (+3 saat ekle)
      if (transactionType?.toLowerCase() === 'transfer') {
        // DB'den gelen tarih UTC ise ama Z suffix'i yoksa, ekle
        if (dateString && !dateString.endsWith('Z') && dateString.includes('T')) {
          adjustedDateString = dateString + 'Z';
        }
      }
      
      const date = new Date(adjustedDateString);
      return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: 'Europe/Istanbul'
      }).format(date);
    } catch (error) {
      return dateString;
    }
  };
  
  // ✅ Transfer işlemlerini sadece bir kez logla
  useEffect(() => {
    const transferTransactions = paginatedTransactions.filter(t => t.transactionType?.toLowerCase() === 'transfer');
    if (transferTransactions.length > 0) {
      console.log('🔍 [TRANSFER] Sayfa yüklendi, transfer işlemleri:');
      transferTransactions.forEach(t => {
        console.log(`  📅 DB Saati: ${t.createdAt} | Gösterilen: ${formatDate(t.createdAt)} | Açıklama: ${t.description}`);
      });
    }
  }, [page]); // Sadece sayfa değiştiğinde çalış
  
  // Sayfa değişimi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Para birimi formatla
  const formatCurrency = (amount: number, currencyCode: string) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Excel'e aktarma fonksiyonu
  const exportToExcel = async () => {
    try {
      setLoadingExport(true);
      
      // Excel workbook oluştur
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t('cash.transactionReport.excel.sheetName'));
      
      // Sütunları tanımla
      worksheet.columns = [
        { header: t('cash.transactionReport.excel.columns.date'), key: 'date', width: 20 },
        { header: t('cash.transactionReport.excel.columns.cashbox'), key: 'cashbox', width: 20 },
        { header: t('cash.transactionReport.excel.columns.branch'), key: 'branchName', width: 20 },
        { header: t('cash.transactionReport.excel.columns.transactionType'), key: 'transactionType', width: 15 },
        { header: t('cash.transactionReport.excel.columns.description'), key: 'description', width: 30 },
        { header: t('cash.transactionReport.excel.columns.amount'), key: 'amount', width: 15 },
        { header: t('cash.transactionReport.excel.columns.balance'), key: 'balance', width: 15 },
        { header: t('cash.transactionReport.excel.columns.payment'), key: 'paymentMethod', width: 15 },
        { header: t('cash.transactionReport.excel.columns.account'), key: 'account', width: 25 }
      ];
      
      // Stil tanımlamaları
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF4F81BD' } },
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        }
      };
      
      // Başlık stilini uygula
      worksheet.getRow(1).eachCell(cell => {
        cell.style = headerStyle;
      });
      
      // Filtrelenmiş verileri ekle
      filteredTransactions.forEach(transaction => {
        const typeName = getTransactionTypeName(transaction.transactionType, transaction.type);

        const amountPrefix = transaction.type === 'INCOME' ? '+' : '-';
        worksheet.addRow({
          date: formatDate(transaction.date, transaction.transactionType),
          cashbox: transaction.cashboxName,
          branchName: transaction.branchName || branches.find(b => b.id === transaction.branchId)?.name || '-',
          transactionType: typeName,
          description: transaction.description,
          amount: `${amountPrefix} ${formatCurrency(transaction.amount, transaction.currency)}`,
          balance: formatCurrency(transaction.balanceAfter || 0, transaction.currency),
          paymentMethod: getPaymentMethodName(transaction.paymentMethod),
          account: transaction.accountName || '-'
        });
      });
      
      // Zebrastripe (alternatif satır renklendirme)
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          const fillColor = rowNumber % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
          row.eachCell(cell => {
            cell.style = {
              fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
              border: {
                top: { style: 'thin' as const },
                left: { style: 'thin' as const },
                bottom: { style: 'thin' as const },
                right: { style: 'thin' as const }
              }
            };
          });
        }
      });
      
      // Dosya adına tarih ekle
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `${t('cash.transactionReport.excel.filePrefix')}_${currentDate}.xlsx`;
      
      // Excel dosyasını oluştur ve indir
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
      
      setError(null); // Varsa hata mesajını temizle
      console.log(`${filteredTransactions.length} adet kasa hareketi Excel dosyasına aktarıldı.`);
      
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      setError(t('cash.errors.exportFailed'));
    } finally {
      setLoadingExport(false);
    }
  };
  
  // Excel için para birimi formatı (₺ sembolü olmadan)
  const formatCurrencyForExcel = (amount: number, currencyCode: string): string => {
    return `${amount.toLocaleString(locale)} ${currencyCode}`;
  };

  // İşlem tipi için ikon döndüren fonksiyon (varsa güncellenecek, yoksa oluşturulacak)
  const getTransactionIcon = (transaction: CashboxTransaction) => {
    const typeKey = (transaction.transactionType?.toUpperCase() || transaction.type?.toUpperCase()) as keyof typeof TRANSACTION_TYPES;
    const type = TRANSACTION_TYPES[typeKey];
    const mainType = transaction.type; // INCOME veya EXPENSE

    switch (type) {
      case TRANSACTION_TYPES.CASH:
      case TRANSACTION_TYPES.DEPOSIT:
      case TRANSACTION_TYPES.COLLECTION:
        return mainType === 'INCOME' ? <TrendingUpIcon fontSize="small" color="success" /> : <TrendingDownIcon fontSize="small" color="error" />;
      case TRANSACTION_TYPES.CREDIT_CARD:
        return <CreditCardIcon fontSize="small" color="primary" />;
      case TRANSACTION_TYPES.DEBIT_CARD:
        return <PaymentIcon fontSize="small" color="secondary" />;
      case TRANSACTION_TYPES.BANK_TRANSFER:
      case TRANSACTION_TYPES.TRANSFER:
        return mainType === 'INCOME' ? <TrendingUpIcon fontSize="small" color="success" /> : <TrendingDownIcon fontSize="small" color="error" />;
      case TRANSACTION_TYPES.QUICK_SALE:
        return <PointOfSaleIcon fontSize="small" color="info" />;
      case TRANSACTION_TYPES.PAYMENT:
      case TRANSACTION_TYPES.WITHDRAWAL:
      case TRANSACTION_TYPES.EXPENSE:
        return <TrendingDownIcon fontSize="small" color="error" />;
      default:
        return mainType === 'INCOME' ? <TrendingUpIcon fontSize="small" color="success" /> : <TrendingDownIcon fontSize="small" color="error" />;
    }
  };

  // Tutar toplamını hesapla
  const calculateTotalAmount = useMemo(() => {
    // Filtreli işlemlerden tutar toplamını hesapla
    let income = 0;
    let expense = 0;
    
    filteredTransactions.forEach(transaction => {
      if (transaction.type === 'INCOME') {
        income += transaction.amount;
      } else {
        expense += transaction.amount;
      }
    });
    
    return { income, expense, total: income - expense };
  }, [filteredTransactions]);

  // ✅ UYARI: Eski frontend hesaplama fonksiyonları kaldırıldı (calculateTodayTotals, calculatePreviousBalance)
  // Artık tüm hesaplamalar backend'de SQL sorgusu ile yapılıyor (ACCOUNTING_API.CASHBOX_DAILY_SUMMARY)

  // AccountingDataTable kolon tanımları
  const transactionColumns = useMemo<DataTableColumn<CashboxTransaction>[]>(() => [
    {
      id: 'rowNumber',
      label: t('cash.transactionReport.table.columns.row'),
      width: '60px',
      render: (_row, index) => (
        <Box component="span" sx={{ color: 'text.secondary' }}>{index + 1}</Box>
      ),
    },
    {
      id: 'date',
      label: t('cash.transactionReport.table.columns.date'),
      sortable: true,
      render: (row) => <>{formatDate(row.date, row.transactionType)}</>,
    },
    {
      id: 'cashboxName',
      label: t('cash.transactionReport.table.columns.cashbox'),
      field: 'cashboxName',
      sortable: true,
      // Proje bağı kasa adının altında küçük etiket olarak gösterilir (PYS)
      render: (row) => (
        <Box>
          <Typography variant="body2" noWrap>{row.cashboxName}</Typography>
          <ProjectBadge projectName={row.projectName} />
        </Box>
      ),
    },
    {
      id: 'branchName',
      label: t('cash.transactionReport.table.columns.branch'),
      sortable: true,
      render: (row) => <>{row.branchName || branches.find(b => b.id === row.branchId)?.name || '-'}</>,
    },
    {
      id: 'transactionType',
      label: t('cash.transactionReport.table.columns.transactionType'),
      sortable: true,
      render: (row) => (
        <Chip
          icon={getTransactionIcon(row)}
          label={getTransactionTypeName(row.transactionType, row.type)}
          size="small"
          color={row.type === 'INCOME' ? 'success' : 'error'}
          sx={{ fontSize: '0.75rem' }}
        />
      ),
    },
    {
      id: 'description',
      label: t('cash.transactionReport.table.columns.description'),
      field: 'description',
      sortable: true,
    },
    {
      id: 'amount',
      label: t('cash.transactionReport.table.columns.amount'),
      field: 'amount',
      align: 'right',
      sortable: true,
      render: (row) => (
        <Box component="span" sx={{ fontWeight: 'medium', color: row.type === 'INCOME' ? 'success.main' : 'error.main' }}>
          {row.type === 'INCOME' ? '+' : '-'} {row.amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getCurrencySymbol(row.currency)}
        </Box>
      ),
    },
    {
      id: 'balanceAfter',
      label: t('cash.transactionReport.table.columns.balance'),
      field: 'balanceAfter',
      align: 'right',
      sortable: true,
      render: (row) => (
        <>{(row.balanceAfter || 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getCurrencySymbol(row.currency)}</>
      ),
    },
    ...(isManager ? [] : [{
      id: 'actions',
      label: t('cash.transactionReport.table.columns.actions'),
      align: 'center' as const,
      render: (row: CashboxTransaction) => (
        <Tooltip title={t('cash.transactionReport.table.columns.actions')}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, row); }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    }]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [branches, locale, t, isManager]);

  // ✅ 3 nokta menüsünü açma
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, transaction: CashboxTransaction) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTransactionForDelete(transaction);
  };

  // ✅ 3 nokta menüsünü kapatma
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // ✅ Silme dialogunu açma
  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  // ✅ Silme dialogunu kapatma
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedTransactionForDelete(null);
  };

  // ✅ İşlemi silme
  const handleDeleteTransaction = async () => {
    if (!selectedTransactionForDelete) return;

    setIsDeleting(true);
    try {
      console.log('🗑️ Kasa işlemi siliniyor:', selectedTransactionForDelete.id);

      // Kullanıcı bilgisini al
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        throw new Error(t('cash.transactionReport.errors.sessionNotFound'));
      }

      // Kullanıcının email'ini al (Cloud Functions)
      const userData = await firebaseDataService.getUserData(uid);
      const userEmail = userData?.email || ownAuthSession.getCurrentEmail() || t('cash.transactionReport.deleteDialog.unknownUser');

      // ✅ MULTI-TENANT: companyId kontrolü
      if (!companyId) {
        throw new Error(t('cash.transactionReport.errors.companyNotFound'));
      }

      await accountingApi.post(
        ACCOUNTING_API.CASHBOX_TRANSACTION_DELETE(parseInt(selectedTransactionForDelete.id)),
        {
          companyId: companyId,
          deletedBy: userEmail,
          reason: t('cash.transactionReport.deleteDialog.manualDeleteReason')
        }
      );

      console.log('✅ Kasa işlemi başarıyla silindi');

      // İşlemleri yeniden yükle
      if (companyId) {
        loadTransactions();
      }

      setSnackbarMessage(t('cash.transactionReport.deleteDialog.deleteSuccess'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
      setSelectedTransactionForDelete(null);
    } catch (error: any) {
      console.error('❌ İşlem silinirken hata:', error);
      setSnackbarMessage(error.response?.data?.message || error.message || t('cash.transactionReport.deleteDialog.deleteFailed'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };

  // ✅ Snackbar'ı kapatma
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Üst Başlık ve Butonlar */}
      <HeaderContainer sx={{ flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 2, md: 0 } }}>
        <Box display="flex" alignItems="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
          <IconButton 
            onClick={() => (onBack ? onBack() : navigate('/accounting/cashbox'))} 
            color="primary"
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <AssessmentIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: 1.5, color: 'primary.main' }} />
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
            {t('cash.transactionReport.title')}
          </Typography>
        </Box>

        {/* Desktop Butonlar */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
            color={showFilters ? "primary" : "inherit"}
          >
            {showFilters ? t('cash.transactionReport.actions.hideFilters') : t('cash.transactionReport.actions.showFilters')}
          </Button>
          {!isManager && (
            <Button 
              variant="outlined" 
              startIcon={<AssessmentIcon />}
              onClick={() => setIsLogsModalOpen(true)}
            >
              {t('cash.transactionReport.actions.logs')}
            </Button>
          )}
          <Button 
            variant="outlined" 
            startIcon={<FileDownloadIcon />}
            onClick={exportToExcel}
            disabled={loadingExport || loading}
          >
            {loadingExport ? t('cash.actions.excelPreparing') : t('cash.actions.downloadExcel')}
          </Button>
        </Box>

        {/* Mobil Butonlar */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1, width: '100%', flexWrap: 'wrap' }}>
          <IconButton
            color="primary"
            onClick={() => setShowFilters(!showFilters)}
            sx={{ 
              bgcolor: showFilters ? 'primary.main' : 'grey.300',
              color: showFilters ? 'white' : 'grey.700',
              '&:hover': { bgcolor: showFilters ? 'primary.dark' : 'grey.400' }
            }}
          >
            <FilterListIcon />
          </IconButton>
          {!isManager && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AssessmentIcon />}
              onClick={() => setIsLogsModalOpen(true)}
              sx={{ flex: 1 }}
            >
              {t('cash.transactionReport.actions.logs')}
            </Button>
          )}
          <IconButton
            color="success"
            onClick={exportToExcel}
            disabled={loadingExport || loading}
            sx={{ 
              bgcolor: 'success.main', 
              color: 'white',
              '&:hover': { bgcolor: 'success.dark' },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
            }}
          >
            <FileDownloadIcon />
          </IconButton>
        </Box>
      </HeaderContainer>

      {/* Arama ve Filtreleme - Tek satırda */}
      {showFilters && (
        <Paper elevation={2} sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {/* Arama */}
            <TextField
              size="small"
              placeholder={t('cash.transactionReport.filters.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 120, maxWidth: 150 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            
            {/* İşlem Türü */}
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel id="filter-transaction-type-label">{t('cash.transactionReport.filters.type')}</InputLabel>
              <Select
                labelId="filter-transaction-type-label"
                value={filterType}
                label={t('cash.transactionReport.filters.type')}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="all">{t('cash.transactionReport.filters.all')}</MenuItem>
                <MenuItem value="INCOME">{t('cash.transactionReport.filters.income')}</MenuItem>
                <MenuItem value="EXPENSE">{t('cash.transactionReport.filters.expense')}</MenuItem>
              </Select>
            </FormControl>
            
            {/* Kasa */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="cashbox-filter-label">{t('cash.transactionReport.filters.cashbox')}</InputLabel>
              <Select
                labelId="cashbox-filter-label"
                value={filterCashbox}
                label={t('cash.transactionReport.filters.cashbox')}
                onChange={(e) => setFilterCashbox(e.target.value)}
              >
                <MenuItem value="">{t('cash.transactionReport.filters.all')}</MenuItem>
                {cashboxes.map(cashbox => (
                  <MenuItem key={cashbox.id} value={cashbox.id}>
                    {cashbox.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Şube Filtresi */}
            {!isManager && <Autocomplete
              multiple
              size="small"
              options={branches}
              getOptionLabel={(option) => option.name}
              value={selectedBranches}
              onChange={(event, newValue) => {
                setSelectedBranches(newValue);
              }}
              sx={{ minWidth: 150, maxWidth: 250 }}
              renderInput={(params) => (
                <TextField {...params} label={t('cash.transactionReport.filters.branch')} placeholder={t('cash.transactionReport.filters.select')} />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    size="small"
                    label={option.name}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />}
            
            {/* Tarih Aralığı */}
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pickerLocale}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <DatePicker
                  label={t('cash.transactionReport.filters.startDate')}
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ 
                    textField: { 
                      size: 'small',
                      sx: { width: 130 }
                    } 
                  }}
                />
                <DatePicker
                  label={t('cash.transactionReport.filters.endDate')}
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ 
                    textField: { 
                      size: 'small',
                      sx: { width: 130 }
                    } 
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Box>
          
          {/* Aktif Filtreler */}
          {(filterType !== 'all' || filterCashbox || selectedBranches.length > 0 || startDate || endDate) && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                <FilterListIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {t('cash.transactionReport.filters.active')}:
              </Typography>
              
              {filterType !== 'all' && (
                <Chip 
                  label={filterType === 'INCOME' ? t('cash.transactionReport.filters.income') : t('cash.transactionReport.filters.expense')}
                  size="small"
                  color={filterType === 'INCOME' ? 'success' : 'error'}
                  onDelete={() => setFilterType('all')}
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
              )}
              
              {filterCashbox && (
                <Chip 
                  label={cashboxes.find(c => c.id === filterCashbox)?.name}
                  size="small"
                  onDelete={() => setFilterCashbox('')}
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
              )}
              
              {/* Seçili şubeler için chip'ler */}
              {selectedBranches.map(branch => (
                <Chip 
                  key={branch.id}
                  label={branch.name}
                  size="small"
                  color="secondary"
                  onDelete={() => setSelectedBranches(selectedBranches.filter(b => b.id !== branch.id))}
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
              ))}
              
              {/* Tarih aralığı için chip */}
              {(startDate || endDate) && (
                <Chip 
                  label={`${startDate ? startDate.toLocaleDateString(locale) : '...'} - ${endDate ? endDate.toLocaleDateString(locale) : '...'}`}
                  size="small"
                  onDelete={() => { setStartDate(null); setEndDate(null); }}
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* Ana İçerik */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Bugünün Toplam Gelir/Gider Özeti — StatsCard Bileşeni */}
      <StatsCardsContainer loading={!dailySummary} cardCount={4}>
        {dailySummary && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCardItem
                icon={<AssessmentIcon />}
                color={STATS_COLORS.blue}
                title={formatStatsCurrency(dailySummary.previousBalance.netBalance)}
                subtitle={t('cash.transactionReport.stats.previousBalance')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCardItem
                icon={<TrendingUpIcon />}
                color={STATS_COLORS.green}
                title={formatStatsCurrency(dailySummary.period.income)}
                subtitle={t('cash.transactionReport.stats.periodIncome')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCardItem
                icon={<TrendingDownIcon />}
                color={STATS_COLORS.red}
                title={formatStatsCurrency(dailySummary.period.expense)}
                subtitle={t('cash.transactionReport.stats.periodExpense')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCardItem
                icon={<PaymentIcon />}
                color={dailySummary.currentBalance >= 0 ? STATS_COLORS.green : STATS_COLORS.red}
                title={`${dailySummary.currentBalance >= 0 ? '+' : ''}${formatStatsCurrency(dailySummary.currentBalance)}`}
                subtitle={t('cash.transactionReport.stats.currentNet')}
              />
            </Grid>
          </>
        )}
      </StatsCardsContainer>
      
      {/* 📱 Mobile: Loading veya boş durum */}
      {loading && filteredTransactions.length === 0 ? (
        <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      ) : filteredTransactions.length === 0 && !loading ? (
        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 4, textAlign: 'center' }}>
          <AssessmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            {searchTerm || filterType !== 'all' || filterCashbox || selectedBranches.length > 0 || startDate || endDate ?
              t('cash.transactionReport.empty.filtered') :
              t('cash.transactionReport.empty.initial')}
          </Typography>
        </Box>
      ) : null}

      {filteredTransactions.length > 0 && (
        <>
          {/* 📱 Mobile Card View (xs, sm) */}
          <Grid container spacing={2} sx={{ display: { xs: 'grid', md: 'none' }, gridTemplateColumns: '1fr', gap: 2, mb: 2 }}>
            {paginatedTransactions.map((transaction) => (
              <Card 
                key={transaction.id}
                elevation={2} 
                sx={{ 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: transaction.type === 'INCOME' ? 'success.light' : 'error.light',
                  bgcolor: transaction.type === 'INCOME' ? 'success.50' : 'error.50',
                  '&:hover': { 
                    boxShadow: 4,
                    borderColor: transaction.type === 'INCOME' ? 'success.main' : 'error.main'
                  }
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {/* İşlem Başlığı */}
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getTransactionIcon(transaction)}
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                        {transaction.cashboxName}
                      </Typography>
                    </Box>
                    {!isManager && (
                      <IconButton 
                        size="small" 
                        onClick={(e) => handleMenuOpen(e, transaction)}
                        sx={{ bgcolor: 'action.hover' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  <Divider sx={{ mb: 1.5 }} />

                  {/* Tarih */}
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="caption" color="text.secondary">{t('cash.transactionReport.table.columns.date')}:</Typography>
                    <Typography variant="caption" fontWeight="medium">{formatDate(transaction.date, transaction.transactionType)}</Typography>
                  </Box>

                  {/* Şube */}
                  {(transaction.branchName || transaction.branchId) && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="caption" color="text.secondary">{t('cash.transactionReport.table.columns.branch')}:</Typography>
                      <Typography variant="caption" fontWeight="medium">
                        {transaction.branchName || branches.find(b => b.id === transaction.branchId)?.name || '-'}
                      </Typography>
                    </Box>
                  )}

                  {/* İşlem Türü */}
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="caption" color="text.secondary">{t('cash.transactionReport.table.columns.transactionType')}:</Typography>
                    <Chip
                      label={getTransactionTypeName(transaction.transactionType, transaction.type)}
                      size="small"
                      color={transaction.type === 'INCOME' ? 'success' : 'error'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>

                  {/* Açıklama */}
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="caption" color="text.secondary">{t('cash.transactionReport.table.columns.description')}:</Typography>
                    <Typography variant="caption" fontWeight="medium" align="right" sx={{ maxWidth: '60%' }}>
                      {transaction.description}
                    </Typography>
                  </Box>

                  {/* Ödeme Yöntemi */}
                  {transaction.paymentMethod && transaction.paymentMethod !== '-' && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="caption" color="text.secondary">{t('cash.transactionReport.table.columns.payment')}:</Typography>
                      <Typography variant="caption" fontWeight="medium">{getPaymentMethodName(transaction.paymentMethod)}</Typography>
                    </Box>
                  )}

                  {/* Cari Hesap */}
                  {transaction.accountName && transaction.accountName !== '-' && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="caption" color="text.secondary">{t('cash.transactionReport.table.columns.account')}:</Typography>
                      <Typography variant="caption" fontWeight="medium">{transaction.accountName}</Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  {/* Tutar ve Bakiye */}
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('cash.transactionReport.table.columns.amount')}:</Typography>
                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        color={transaction.type === 'INCOME' ? 'success.main' : 'error.main'}
                        sx={{ fontSize: '1.1rem' }}
                      >
                        {transaction.type === 'INCOME' ? '+' : '-'} 
                        {transaction.amount.toLocaleString(locale, { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        })} {getCurrencySymbol(transaction.currency)}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="caption" color="text.secondary">{t('cash.transactionReport.table.columns.balance')}:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {(transaction.balanceAfter || 0).toLocaleString(locale, { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        })} {getCurrencySymbol(transaction.currency)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Grid>

        </>
      )}

      {/* Desktop: AccountingDataTable */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <AccountingDataTable<CashboxTransaction>
          columns={transactionColumns}
          rows={filteredTransactions}
          getRowId={(row) => row.id}
          loading={loading}
          serverSidePagination={true}
          totalCount={totalCount}
          page={page}
          pageSize={rowsPerPage}
          onPageChange={(newPage, newPageSize) => {
            setPage(newPage);
            setRowsPerPage(newPageSize);
          }}
          onSort={(field, direction) => {
            setSortColumn(field);
            setSortDirection(direction);
          }}
          sortField={sortColumn}
          sortDirection={sortDirection}
          emptyMessage={
            searchTerm || filterType !== 'all' || filterCashbox || selectedBranches.length > 0 || startDate || endDate
              ? t('cash.transactionReport.empty.filtered')
              : t('cash.transactionReport.empty.initial')
          }
          emptyIcon={<PointOfSaleIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
          ariaLabel={t('cash.transactionReport.tableAriaLabel')}
          columnResizeStorageKey="cashbox-transactions"
        />
        {/* Genel Toplam */}
        {filteredTransactions.length > 0 && (
          <Paper elevation={1} sx={{ mt: 0, p: 1.5, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, borderRadius: '0 0 8px 8px' }}>
            <Typography variant="subtitle2" fontWeight="bold">{t('cash.transactionReport.totalLabel')}</Typography>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: calculateTotalAmount.total >= 0 ? 'success.main' : 'error.main' }}>
              {calculateTotalAmount.total >= 0 ? '+' : ''}{calculateTotalAmount.total.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
            </Typography>
          </Paper>
        )}
      </Box>

      {/* ✅ 3 Nokta Menüsü */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleDeleteDialogOpen}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          {t('cash.actions.delete')}
        </MenuItem>
      </Menu>

      {/* ✅ Silme Onay Dialogu */}
      <Dialog
        open={deleteDialogOpen}
        onClose={!isDeleting ? handleDeleteDialogClose : undefined}
        maxWidth="sm"
        fullWidth
      >
        {(() => {
          // Entegrasyon işlemi kontrolü - transactionType veya açıklamada fatura numarası varsa
          const isIntegrationTransaction = 
            selectedTransactionForDelete?.transactionType === 'QUICK_SALE' || 
            selectedTransactionForDelete?.transactionType === 'SALES_INVOICE' ||
            selectedTransactionForDelete?.transactionType === 'PURCHASE_INVOICE' ||
            // Açıklamada fatura numarası pattern'i varsa (SF-XXXXXX veya AF-XXXXXX)
            /SF-\d{6}-\d{4}|AF-\d{6}-\d{4}/.test(selectedTransactionForDelete?.description || '');
          
          // Fatura numarasını açıklamadan çıkar
          const invoiceNumberMatch = selectedTransactionForDelete?.description?.match(/[SA]F-\d{6}-\d{4}/);
          const extractedInvoiceNumber = invoiceNumberMatch ? invoiceNumberMatch[0] : null;

          return (
            <>
              <DialogTitle
                sx={{
                  bgcolor: isIntegrationTransaction ? 'warning.main' : 'error.main',
                  color: 'white'
                }}
              >
                {isIntegrationTransaction ? t('cash.transactionReport.deleteDialog.integrationTitle') : t('cash.transactionReport.deleteDialog.title')}
              </DialogTitle>
              <DialogContent>
                <DialogContentText component="div">
                  {/* Entegrasyon işlemleri için özel uyarı */}
                  {isIntegrationTransaction ? (
                    <>
                      <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {t('cash.transactionReport.deleteDialog.integrationCannotDelete')}
                        </Typography>
                      </Alert>
                      
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          {t('cash.transactionReport.deleteDialog.integrationDescription')}
                        </Typography>
                        {extractedInvoiceNumber && (
                          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                            {t('cash.transactionReport.deleteDialog.invoiceNumber', { number: extractedInvoiceNumber })}
                          </Typography>
                        )}
                      </Alert>

                      {selectedTransactionForDelete && (
                        <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('cash.transactionReport.deleteDialog.cashbox')}:</strong> {selectedTransactionForDelete.cashboxName}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('cash.transactionReport.deleteDialog.amount')}:</strong> {selectedTransactionForDelete.amount.toLocaleString(locale)} {getCurrencySymbol(selectedTransactionForDelete.currency)}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('cash.transactionReport.deleteDialog.transactionType')}:</strong> {getTransactionTypeName(selectedTransactionForDelete.transactionType, selectedTransactionForDelete.type)}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('cash.transactionReport.table.columns.description')}:</strong> {selectedTransactionForDelete.description}
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <>
                      <Alert severity="success" sx={{ mb: 2, mt: 2 }}>
                        {t('cash.transactionReport.deleteDialog.reverseInfo')}
                      </Alert>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        {t('cash.transactionReport.deleteDialog.warning')}
                      </Alert>
                      {selectedTransactionForDelete && (
                        <Box>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('cash.transactionReport.deleteDialog.cashbox')}:</strong> {selectedTransactionForDelete.cashboxName}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('cash.transactionReport.deleteDialog.transactionType')}:</strong> {selectedTransactionForDelete.type === 'INCOME' ? t('cash.transactionReport.filters.income') : t('cash.transactionReport.filters.expense')}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('cash.transactionReport.deleteDialog.amount')}:</strong> {selectedTransactionForDelete.amount.toLocaleString(locale)} {getCurrencySymbol(selectedTransactionForDelete.currency)}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('cash.transactionReport.table.columns.description')}:</strong> {selectedTransactionForDelete.description}
                          </Typography>
                          
                          <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {selectedTransactionForDelete.type === 'INCOME' 
                                ? t('cash.transactionReport.deleteDialog.incomeDeleteEffect')
                                : t('cash.transactionReport.deleteDialog.expenseDeleteEffect')}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {t('cash.transactionReport.deleteDialog.balanceChange')}: {selectedTransactionForDelete.type === 'INCOME' ? '-' : '+'}{selectedTransactionForDelete.amount.toLocaleString(locale)} {getCurrencySymbol(selectedTransactionForDelete.currency)}
                            </Typography>
                          </Alert>
                          
                          <Typography variant="body2" sx={{ mt: 2, color: 'error.main', fontWeight: 'bold' }}>
                            {t('cash.transactionReport.deleteDialog.confirm')}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleDeleteDialogClose} disabled={isDeleting}>
                  {isIntegrationTransaction ? t('cash.common.close') : t('cash.common.cancel')}
                </Button>
                
                {/* Entegrasyon işlemleri için Fatura Yönetimi butonu */}
                {isIntegrationTransaction ? (
                  <Button
                    onClick={() => {
                      handleDeleteDialogClose();
                      navigate('/fatura/satis');
                    }}
                    variant="contained"
                    color="primary"
                    startIcon={<ReceiptIcon />}
                  >
                    {t('cash.transactionReport.deleteDialog.goInvoice')}
                  </Button>
                ) : (
                  <Button
                    onClick={handleDeleteTransaction}
                    color="error"
                    variant="contained"
                    disabled={isDeleting}
                    startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                  >
                    {isDeleting ? t('cash.common.deleting') : t('cash.actions.delete')}
                  </Button>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ✅ Snackbar (Bildirim) */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* ✅ Silinen İşlem Logları Modalı */}
      <CashboxTransactionLogsModal
        open={isLogsModalOpen}
        onClose={() => setIsLogsModalOpen(false)}
        companyId={companyId}
      />
    </Box>
  );
};

export default CashboxTransactions; 